import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useCreateCampaign } from '../hooks/DataFetching/useCreateCampaign';
import { getApiBaseUrl } from '../config/apiBase';
import { calculateVoteRequirements, evaluateIdeaImpact } from '../lib/ideaImpactPolicy';
import '../styles/components/PostCampaign.css';
import { FEATURE_FLAGS, isFeatureEnabled } from '../config/featureFlags';

const ideaTypes = [
  { value: 'action', label: 'Action or project', hint: 'A thing the group may do together.' },
  { value: 'calendarEvent', label: 'Event or gathering', hint: 'Something scheduled on the calendar.' },
  { value: 'gift', label: 'Purchase or gift', hint: 'Money, supplies, or direct support.' },
  { value: 'policy', label: 'Policy or rule', hint: 'A standing rule the group will live under.' },
  { value: 'position', label: 'Role or position', hint: 'A responsibility or office someone will fill.' },
  { value: 'petition', label: 'Signal of support', hint: 'A non-binding temperature check or shared statement.' },
];

const ideaShapes = [
  { value: 'simple', label: 'Simple idea', hint: 'A low-burden suggestion or small improvement.' },
  { value: 'event', label: 'Event or activity', hint: 'A meal, outreach, service, class, dinner, or gathering.' },
  { value: 'fundraiser', label: 'Fundraiser', hint: 'A bake sale, giving drive, or effort to raise designated funds.' },
  { value: 'serviceProject', label: 'Service project', hint: 'Hands-on help for a person, household, ministry, or neighbor.' },
  { value: 'purchase', label: 'Purchase or equipment', hint: 'Buying or receiving something the group may own or use.' },
  { value: 'externalGift', label: 'External mercy gift', hint: 'Funding help, wells, supplies, or partner work outside the group.' },
  { value: 'institution', label: 'School or institution', hint: 'A staged, durable effort like a school, ministry, or long-term program.' },
  { value: 'crisisAction', label: 'Courageous/crisis action', hint: 'A serious intervention in danger, abuse, trafficking, or urgent harm.' },
  { value: 'policy', label: 'Living policy', hint: 'A rule the group can keep revisiting and changing together.' },
];

const fundingPlanOptions = [
  { value: 'none', label: 'No money needed' },
  { value: 'treasury', label: 'Use group treasury funds' },
  { value: 'separate', label: 'Raise money separately' },
  { value: 'mixed', label: 'Mix treasury, gifts, cash/check, online giving, or donated items' },
  { value: 'unknown', label: 'Not sure yet' },
];

const initialIdea = {
  title: '',
  proposal: '',
  campaignType: 'action',
  purpose: '',
  affectedScope: 'volunteers',
  ideaShape: 'simple',
  ideaSubtype: '',
  fundingPlan: 'none',
  duration: 'one-time',
  groupFundsAmount: '',
  groupFundsPercent: '',
  raisesSeparateFunds: false,
  separateRaiseAmount: '',
  publicFundraising: false,
  treasuryFundingRequested: false,
  treasuryFundingCap: '',
  separateFundingGoal: '',
  inventoryAcquisitionRequested: false,
  inventoryStewardName: '',
  inventoryStorageLocation: '',
  externalPartnerName: '',
  externalPartnerVerification: '',
  proposalPhase: 'proposal',
  safetyPlanSummary: '',
  volunteerCount: '',
  usesGroupNamePublicly: false,
  changesPolicy: false,
  resourceFlags: [],
  riskFlags: [],
  requiredRoles: [],
  expirationDays: 7,
};

const aiInterviewPrompts = [
  'I’m here to help you shape this into a clear idea for the group. Tell me what you’re imagining, even if it is messy or incomplete.',
  'That helps. Who is this meant to bless or affect, and what need are you hoping the group can answer?',
  'What would it take in real life — timing, place, money, supplies, helpers, or responsibilities?',
  'Is there anything the group should notice before voting, like children or vulnerable people, public use of the group’s name, safety, privacy, policy, or long-term commitment?',
  'What would make this easy for the group to understand and decide on faithfully?',
];

const initialAiMessages = [
  {
    role: 'assistant',
    content: aiInterviewPrompts[0],
  },
];

const checkboxOptions = {
  resourceFlags: [
    ['money', 'Money or supplies'],
    ['materials', 'Materials or equipment'],
    ['calendar', 'Date or location'],
    ['public', 'Public invitation'],
    ['outside-partner', 'Another group/church'],
  ],
  requiredRoles: [
    ['coordinator', 'Coordinator'],
    ['treasurer', 'Treasurer/steward'],
    ['childcare', 'Childcare/screened care'],
    ['safety', 'Safety lead'],
    ['transportation', 'Transportation driver'],
    ['spokesperson', 'Public spokesperson'],
    ['legal', 'Legal signer/reviewer'],
  ],
  riskFlags: [
    ['children', 'Children or vulnerable people'],
    ['safety', 'Safety risk'],
    ['travel', 'Travel/transportation'],
    ['privacy', 'Private information'],
    ['legal', 'Legal exposure'],
    ['property', 'Property/lease/asset'],
    ['debt', 'Debt or long-term liability'],
    ['danger', 'Active danger or hostile environment'],
    ['law-enforcement', 'Law enforcement / official coordination'],
    ['trafficking', 'Exploitation / trafficking context'],
  ],
};

const scopeLabels = {
  volunteers: 'Only people who choose to help',
  team: 'A ministry/team',
  'whole-group': 'The whole group',
  public: 'Neighbors or the public',
  'multi-group': 'Another church/group too',
};

const durationLabels = {
  'one-time': 'One-time',
  short: 'Short season',
  ongoing: 'Ongoing',
  permanent: 'Permanent or hard to unwind',
};

const getDefaultExpiration = (days) => {
  const expiration = new Date();
  expiration.setDate(expiration.getDate() + Number(days || 7));
  return expiration.toISOString();
};

const updateArrayValue = (values, nextValue, checked) => {
  const current = Array.isArray(values) ? values : [];
  if (checked) {
    return Array.from(new Set([...current, nextValue]));
  }
  return current.filter((item) => item !== nextValue);
};

const inferFromAiNotes = (idea) => {
  const notes = `${idea.proposal} ${idea.purpose} ${idea.needs || ''} ${idea.risks || ''}`.toLowerCase();
  return {
    ...idea,
    usesGroupNamePublicly: idea.usesGroupNamePublicly || /public|neighbor|community|invite|outreach|church name|group name/.test(notes),
    raisesSeparateFunds: idea.raisesSeparateFunds || /fundrais|donat|raise money|offering|contribution/.test(notes),
    treasuryFundingRequested: idea.treasuryFundingRequested || /group funds|treasury|budget/.test(notes),
    inventoryAcquisitionRequested: idea.inventoryAcquisitionRequested || /inventory|owned by the group|keyboard|equipment|buy|purchase/.test(notes),
    changesPolicy: idea.changesPolicy || /policy|rule|membership standard|governance|treasury rule|doctrine/.test(notes),
    volunteerCount: idea.volunteerCount || (/(many|team|several|helpers|volunteers)/.test(notes) ? '8' : ''),
    resourceFlags: Array.from(new Set([
      ...(idea.resourceFlags || []),
      ...(/money|budget|fund|supplies|food|materials|cups|tables/.test(notes) ? ['money', 'materials'] : []),
      ...(/date|calendar|night|event|gathering/.test(notes) ? ['calendar'] : []),
      ...(/public|neighbor|community|outreach|invite/.test(notes) ? ['public'] : []),
    ])),
    requiredRoles: Array.from(new Set([
      ...(idea.requiredRoles || []),
      ...(/coordinator|lead|organizer/.test(notes) ? ['coordinator'] : []),
      ...(/treasurer|steward|money/.test(notes) ? ['treasurer'] : []),
      ...(/child|family|families/.test(notes) ? ['childcare'] : []),
    ])),
    riskFlags: Array.from(new Set([
      ...(idea.riskFlags || []),
      ...(/child|family|families|vulnerable/.test(notes) ? ['children'] : []),
      ...(/safety|food safety|cleanup|weather/.test(notes) ? ['safety'] : []),
      ...(/travel|transport/.test(notes) ? ['travel'] : []),
      ...(/traffick|prostitution|rescue|shut down|danger|hostile/.test(notes) ? ['danger', 'trafficking', 'law-enforcement'] : []),
    ])),
    ideaShape: idea.ideaShape !== 'simple'
      ? idea.ideaShape
      : /bake sale|fundrais|raise money|donation|offering/.test(notes)
        ? 'fundraiser'
        : /dinner|christmas|cocoa|carol|bonfire|event|gathering|meal/.test(notes)
          ? 'event'
          : /roof|repair|fix|serve|volunteer to go/.test(notes)
            ? 'serviceProject'
            : /keyboard|equipment|buy|purchase|inventory/.test(notes)
              ? 'purchase'
              : /well|driller|nigeria|external|partner/.test(notes)
                ? 'externalGift'
                : /school|students|education|institution/.test(notes)
                  ? 'institution'
                  : /traffick|prostitution|bordello|rescue|shut down/.test(notes)
                    ? 'crisisAction'
                    : /policy|rule|bylaw/.test(notes)
                      ? 'policy'
                      : idea.ideaShape,
    fundingPlan: idea.fundingPlan !== 'none'
      ? idea.fundingPlan
      : /group funds|treasury|budget/.test(notes)
        ? 'treasury'
        : /fundrais|donat|raise money|offering|contribution|stripe|cash|check|btc/.test(notes)
          ? 'separate'
          : idea.fundingPlan,
    affectedScope: idea.affectedScope === 'volunteers' && /public|neighbor|community|outreach/.test(notes)
      ? 'public'
      : idea.affectedScope,
  };
};

const summarizeConversation = (messages) => messages
  .filter((message) => message.role === 'user')
  .map((message) => message.content)
  .join('\n');

const inferCampaignTypeFromText = (text) => {
  const lower = text.toLowerCase();
  if (/policy|rule|bylaw|standard|covenant/.test(lower)) return 'policy';
  if (/event|gathering|night|meal|service|calendar|christmas|concert|outreach/.test(lower)) return 'calendarEvent';
  if (/purchase|buy|gift|equipment|suppl(y|ies)|spend/.test(lower)) return 'gift';
  if (/role|position|appoint|treasurer|coordinator|elder|deacon/.test(lower)) return 'position';
  if (/support|statement|petition|temperature check/.test(lower)) return 'petition';
  return 'action';
};

const buildIdeaFromInterview = (currentIdea, messages) => {
  const transcript = summarizeConversation(messages);
  const combined = `${currentIdea.title} ${currentIdea.proposal} ${currentIdea.purpose} ${transcript}`.toLowerCase();
  const userMessages = messages.filter((message) => message.role === 'user');
  const firstAnswer = userMessages[0]?.content?.trim() || '';
  const secondAnswer = userMessages[1]?.content?.trim() || '';

  const moneyMatch = combined.match(/\$?\b([1-9]\d{1,5})(?:\.\d{1,2})?\b(?=\s*(dollars|usd|for|to|budget|fund|raise|supplies|food|gift|cost)?)/i);
  const percentMatch = combined.match(/\b([1-9]\d?|100)\s*%/);
  const volunteerMatch = combined.match(/\b([1-9]\d?)\s*(helpers|volunteers|people|members|drivers|workers)\b/i);

  const nextIdea = inferFromAiNotes({
    ...currentIdea,
    title: currentIdea.title || firstAnswer.split(/[.!?\n]/)[0].slice(0, 80),
    proposal: currentIdea.proposal || firstAnswer || transcript,
    purpose: currentIdea.purpose || secondAnswer,
    campaignType: currentIdea.campaignType === initialIdea.campaignType
      ? inferCampaignTypeFromText(combined)
      : currentIdea.campaignType,
    groupFundsAmount: currentIdea.groupFundsAmount || (/group funds|treasury|budget|spend|buy|purchase/.test(combined) && moneyMatch ? moneyMatch[1] : ''),
    groupFundsPercent: currentIdea.groupFundsPercent || (percentMatch ? percentMatch[1] : ''),
    raisesSeparateFunds: currentIdea.raisesSeparateFunds || /raise|donation|offering|contribution|fundrais|stripe/.test(combined),
    separateRaiseAmount: currentIdea.separateRaiseAmount || (/raise|donation|offering|contribution|fundrais|stripe/.test(combined) && moneyMatch ? moneyMatch[1] : ''),
    separateFundingGoal: currentIdea.separateFundingGoal || (/raise|donation|offering|contribution|fundrais|stripe|well/.test(combined) && moneyMatch ? moneyMatch[1] : ''),
    treasuryFundingRequested: currentIdea.treasuryFundingRequested || /group funds|treasury|budget/.test(combined),
    treasuryFundingCap: currentIdea.treasuryFundingCap || (/group funds|treasury|budget/.test(combined) && moneyMatch ? moneyMatch[1] : ''),
    inventoryAcquisitionRequested: currentIdea.inventoryAcquisitionRequested || /keyboard|equipment|inventory|buy|purchase/.test(combined),
    publicFundraising: currentIdea.publicFundraising || /public fundrais|community donation|neighbors can give|public donation/.test(combined),
    volunteerCount: currentIdea.volunteerCount || (volunteerMatch ? volunteerMatch[1] : ''),
    usesGroupNamePublicly: currentIdea.usesGroupNamePublicly || /public|community|neighbor|flyer|advertis|group name|church name|social media/.test(combined),
    changesPolicy: currentIdea.changesPolicy || /policy|rule|bylaw|standing|permanent|covenant/.test(combined),
    externalPartnerName: currentIdea.externalPartnerName || (/driller|contractor|partner|vendor/.test(combined) ? 'External partner/vendor mentioned' : ''),
    safetyPlanSummary: currentIdea.safetyPlanSummary || (/roof|bonfire|child|traffick|prostitution|danger|travel/.test(combined) ? 'Safety details should be clarified before voting.' : ''),
    affectedScope: /whole group|everyone|all members|membership/.test(combined)
      ? 'whole-group'
      : /public|neighbor|community|outside/.test(combined)
        ? 'public'
        : /another group|other church|multi-group|partner/.test(combined)
          ? 'multi-group'
          : currentIdea.affectedScope,
    duration: /permanent|ongoing|every year|standing/.test(combined)
      ? 'permanent'
      : /season|several weeks|month/.test(combined)
        ? 'short'
        : currentIdea.duration,
  });

  return nextIdea;
};

const truthyDraftValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'boolean') {
    return true;
  }

  return value !== undefined && value !== null && String(value).trim() !== '';
};

const normalizeStringNumber = (value) => {
  if (value === undefined || value === null) {
    return '';
  }

  const match = String(value).match(/[0-9]+(?:\.[0-9]+)?/);
  return match ? match[0] : '';
};

const allowedDraftValues = {
  campaignType: ideaTypes.map((type) => type.value),
  ideaShape: ideaShapes.map((shape) => shape.value),
  fundingPlan: fundingPlanOptions.map((option) => option.value),
  affectedScope: Object.keys(scopeLabels),
  duration: Object.keys(durationLabels),
  resourceFlags: checkboxOptions.resourceFlags.map(([value]) => value),
  riskFlags: checkboxOptions.riskFlags.map(([value]) => value),
  requiredRoles: checkboxOptions.requiredRoles.map(([value]) => value),
};

const mergeIdeaDraft = (currentIdea, draftIdea = {}) => {
  if (!draftIdea || typeof draftIdea !== 'object') {
    return currentIdea;
  }

  const nextIdea = { ...currentIdea };
  const stringFields = [
    'title',
    'proposal',
    'purpose',
    'ideaSubtype',
    'inventoryStewardName',
    'inventoryStorageLocation',
    'externalPartnerName',
    'externalPartnerVerification',
    'proposalPhase',
    'safetyPlanSummary',
  ];
  stringFields.forEach((field) => {
    if (truthyDraftValue(draftIdea[field])) {
      nextIdea[field] = String(draftIdea[field]).trim();
    }
  });

  ['campaignType', 'ideaShape', 'fundingPlan', 'affectedScope', 'duration'].forEach((field) => {
    if (allowedDraftValues[field].includes(draftIdea[field])) {
      nextIdea[field] = draftIdea[field];
    }
  });

  ['groupFundsAmount', 'groupFundsPercent', 'separateRaiseAmount', 'separateFundingGoal', 'treasuryFundingCap', 'volunteerCount'].forEach((field) => {
    const normalized = normalizeStringNumber(draftIdea[field]);
    if (normalized) {
      nextIdea[field] = normalized;
    }
  });

  ['raisesSeparateFunds', 'publicFundraising', 'usesGroupNamePublicly', 'changesPolicy', 'treasuryFundingRequested', 'inventoryAcquisitionRequested'].forEach((field) => {
    if (typeof draftIdea[field] === 'boolean') {
      nextIdea[field] = draftIdea[field];
    }
  });

  ['resourceFlags', 'riskFlags', 'requiredRoles'].forEach((field) => {
    if (Array.isArray(draftIdea[field])) {
      const allowed = allowedDraftValues[field];
      nextIdea[field] = Array.from(new Set([
        ...(nextIdea[field] || []),
        ...draftIdea[field].filter((value) => allowed.includes(value)),
      ]));
    }
  });

  return inferFromAiNotes(nextIdea);
};

const describeList = (values, options) => {
  const optionMap = new Map(options.map(([value, label]) => [value, label]));
  const labels = (Array.isArray(values) ? values : [])
    .map((value) => optionMap.get(value) || value)
    .filter(Boolean);

  return labels.length ? labels.join(', ') : 'Not identified yet';
};

export const PostCampaign = ({ groupId, problemId = null, eligibleMembers = 120 }) => {
    const bitcoinEnabled = isFeatureEnabled(FEATURE_FLAGS.bitcoinExperience);
  const displayedFundingPlanOptions = fundingPlanOptions.map((option) => option.value === 'mixed' && bitcoinEnabled
    ? { ...option, label: 'Mix treasury, gifts, cash/check, online giving, BTC, or donated items' }
    : option);
  const { isLoading, createCampaign } = useCreateCampaign();
  const { user, getAccessTokenSilently } = useAuth0();

  const [showModal, setShowModal] = useState(false);
  const [entryMode, setEntryMode] = useState('ai');
  const [step, setStep] = useState('compose');
  const [aiStep, setAiStep] = useState(0);
  const [aiMessages, setAiMessages] = useState(initialAiMessages);
  const [aiInput, setAiInput] = useState('');
  const [aiReadyToDraft, setAiReadyToDraft] = useState(false);
  const [aiMissingFields, setAiMissingFields] = useState([]);
  const [guideRecommendedPath, setGuideRecommendedPath] = useState('idea');
  const [idea, setIdea] = useState(initialIdea);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [lastImpactLevel, setLastImpactLevel] = useState(null);
  const [guideReview, setGuideReview] = useState(null);
  const [isReviewingWithGuide, setIsReviewingWithGuide] = useState(false);
  const aiChatRef = useRef(null);

  useEffect(() => {
    const chat = aiChatRef.current;
    if (chat) chat.scrollTop = chat.scrollHeight;
  }, [aiMessages, isReviewingWithGuide]);

  useEffect(() => {
    if (!showModal || typeof document === 'undefined') {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showModal]);

  const assessedIdea = useMemo(
    () => (entryMode === 'ai' ? inferFromAiNotes(idea) : idea),
    [entryMode, idea],
  );

  const impact = useMemo(() => evaluateIdeaImpact(assessedIdea), [assessedIdea]);
  const voteRequirements = useMemo(
    () => calculateVoteRequirements(impact.level, eligibleMembers),
    [impact.level, eligibleMembers],
  );

  const updateIdea = (key, value) => {
    setIdea((current) => ({ ...current, [key]: value }));
  };

  const closeModal = () => {
    setShowModal(false);
    setStep('compose');
    setEntryMode('ai');
    setAiStep(0);
    setAiMessages(initialAiMessages);
    setAiInput('');
    setAiReadyToDraft(false);
    setAiMissingFields([]);
    setGuideRecommendedPath('idea');
    setErrors({});
    setError('');
    setNotice('');
    setLastImpactLevel(null);
  };

  const requestIdeaInterviewTurn = async (messages) => {
    try {
      let token = null;
      if (typeof getAccessTokenSilently === 'function') {
        token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_API_AUDIENCE,
          },
        });
      }

      const response = await fetch(`${getApiBaseUrl()}/api/socraticguide/idea-interview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          messages,
          currentIdea: idea,
          eligibleMembers,
        }),
      });

      if (!response.ok) {
        return null;
      }

      return response.json();
    } catch {
      return null;
    }
  };

  const sendAiInterviewAnswer = async () => {
    const answer = aiInput.trim();
    if (!answer) {
      return;
    }

    const nextStep = Math.min(aiStep + 1, aiInterviewPrompts.length);
    const userMessages = [
      ...aiMessages,
      { role: 'user', content: answer },
    ];

    setAiInput('');
    setAiMessages(userMessages);
    setIsReviewingWithGuide(true);
    const guideTurn = await requestIdeaInterviewTurn(userMessages);
    setIsReviewingWithGuide(false);

    const assistantMessage = guideTurn?.assistantMessage || (
      nextStep < aiInterviewPrompts.length
        ? aiInterviewPrompts[nextStep]
        : 'I think I have enough to shape a solid first draft. You can use it now, or keep talking if there is more context I should understand.'
    );
    const isReadyToDraft = Boolean(guideTurn?.isReadyToDraft || guideTurn?.readyToDraft) || nextStep >= aiInterviewPrompts.length;
    const guideDraft = guideTurn?.draftIdea || guideTurn?.draft || null;
    const guideMissingFields = Array.isArray(guideTurn?.missingFields) ? guideTurn.missingFields : [];
    const interviewText = userMessages.filter(message => message.role === 'user').map(message => message.content).join(' ');
    const soundsPersonal = /(?:picnic|dinner|coffee|game night|cookout|barbecue|bbq).{0,45}(?:my house|my home|our house|our home)/i.test(interviewText)
      && !/(?:group funds|church funds|official|on behalf of|require volunteers|assign people)/i.test(interviewText);
    setGuideRecommendedPath(guideTurn?.recommendedPath === 'memberInvitation' || (!guideTurn && soundsPersonal) ? 'memberInvitation' : 'idea');
    if (guideDraft) {
      setIdea((current) => mergeIdeaDraft(current, guideDraft));
    } else {
      setIdea((current) => buildIdeaFromInterview(current, userMessages));
    }

    const nextMessages = [
      ...userMessages,
      { role: 'assistant', content: assistantMessage },
    ];

    setAiMessages(nextMessages);
    setAiReadyToDraft(isReadyToDraft);
    setAiMissingFields(guideMissingFields);
    setAiStep(nextStep);
  };

  const applyAiInterviewDraft = () => {
    const draftedIdea = buildIdeaFromInterview(idea, aiMessages);
    setIdea(draftedIdea);
    setEntryMode('guided');
    setAiStep(0);
    setAiInput('');
    setAiReadyToDraft(false);
    setAiMissingFields([]);
    setGuideRecommendedPath('idea');
    setNotice('I drafted the form from the interview. Please make any edits, then review the final idea and impact path.');
    setError('');
  };

  const validate = () => {
    const nextErrors = {};
    const finalIdea = entryMode === 'ai' ? inferFromAiNotes(idea) : idea;

    if (!finalIdea.title.trim()) {
      nextErrors.title = 'Give the idea a short title.';
    }
    if (!finalIdea.proposal.trim()) {
      nextErrors.proposal = 'Describe what the group is being asked to consider.';
    }
    if (finalIdea.campaignType === 'policy' && impact.level !== 'Policy') {
      nextErrors.impact = 'Policy ideas must be classified as Policy.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const requestKingdomGuideReview = async (finalIdea) => {
    if (typeof fetch !== 'function') {
      return null;
    }

    try {
      setIsReviewingWithGuide(true);
      let token = null;
      if (typeof getAccessTokenSilently === 'function') {
        token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_API_AUDIENCE,
          },
        });
      }

      const response = await fetch(`${getApiBaseUrl()}/api/socraticguide/idea-review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          idea: finalIdea,
          deterministicImpact: impact,
          eligibleMembers,
        }),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data?.review || null;
    } catch {
      return null;
    } finally {
      setIsReviewingWithGuide(false);
    }
  };

  const reviewIdea = async () => {
    if (!validate()) {
      return;
    }

    const finalIdea = entryMode === 'ai' ? inferFromAiNotes(idea) : idea;
    const nextGuideReview = await requestKingdomGuideReview(finalIdea);
    setGuideReview(nextGuideReview);

    if (lastImpactLevel && lastImpactLevel !== impact.level) {
      setError(`Your edits changed this idea from ${lastImpactLevel} to ${impact.level}. Review the updated voting path before submitting.`);
    } else {
      setError('');
    }

    setLastImpactLevel(impact.level);
    setStep('review');
  };

  const handleSubmit = async () => {
    if (!validate()) {
      setStep('compose');
      return;
    }

    const finalIdea = entryMode === 'ai' ? inferFromAiNotes(idea) : idea;
    const assessmentJson = JSON.stringify({
      answers: finalIdea,
      impact,
      voteRequirements,
      guideReview,
      reviewedBy: 'idea-impact-review-v1',
    });
    const shapeDetailsJson = JSON.stringify({
      ideaShape: finalIdea.ideaShape,
      ideaSubtype: finalIdea.ideaSubtype,
      fundingPlan: finalIdea.fundingPlan,
      examplesCovered: [
        'fundraiser',
        'service project',
        'inter-group event',
        'purchase/inventory',
        'international mercy',
        'crisis action',
        'institution',
        'policy',
      ],
      treasuryFundingRequested: finalIdea.treasuryFundingRequested,
      inventoryAcquisitionRequested: finalIdea.inventoryAcquisitionRequested,
      externalPartnerName: finalIdea.externalPartnerName,
      proposalPhase: finalIdea.proposalPhase,
      safetyPlanSummary: finalIdea.safetyPlanSummary,
    });

    const campaignData = {
      userId: user?.sub,
      groupId,
      problemId,
      campaignType: finalIdea.campaignType,
      title: finalIdea.title.trim(),
      proposal: finalIdea.proposal.trim(),
      fundingInitial: finalIdea.groupFundsAmount ? parseFloat(finalIdea.groupFundsAmount) : null,
      expiration: getDefaultExpiration(impact.deadlineDays),
      impactLevel: impact.level,
      impactSummary: impact.summary,
      impactReasons: impact.reasons.join('\n'),
      impactAssessmentJson: assessmentJson,
      participationThresholdPercent: impact.participationThresholdPercent,
      approvalThresholdPercent: impact.approvalThresholdPercent,
      approvalBasis: impact.approvalBasis,
      votingWindowDays: impact.deadlineDays,
      groupFundsPercentEstimate: finalIdea.groupFundsPercent ? parseFloat(finalIdea.groupFundsPercent) : null,
      separatelyRaisedFunds: finalIdea.raisesSeparateFunds,
      requiredRoles: finalIdea.requiredRoles?.join(', ') || null,
      volunteerEstimate: finalIdea.volunteerCount ? parseInt(finalIdea.volunteerCount, 10) : null,
      affectedScope: finalIdea.affectedScope,
      publicRepresentation: finalIdea.usesGroupNamePublicly,
      riskFlags: finalIdea.riskFlags?.join(', ') || null,
      duration: finalIdea.duration,
      ideaShape: finalIdea.ideaShape,
      ideaSubtype: finalIdea.ideaSubtype || null,
      fundingPlan: finalIdea.fundingPlan,
      treasuryFundingRequested: Boolean(finalIdea.treasuryFundingRequested || finalIdea.fundingPlan === 'treasury' || finalIdea.fundingPlan === 'mixed'),
      treasuryFundingCap: finalIdea.treasuryFundingCap ? parseFloat(finalIdea.treasuryFundingCap) : null,
      separateFundingGoal: finalIdea.separateFundingGoal || finalIdea.separateRaiseAmount
        ? parseFloat(finalIdea.separateFundingGoal || finalIdea.separateRaiseAmount)
        : null,
      inventoryAcquisitionRequested: Boolean(finalIdea.inventoryAcquisitionRequested),
      inventoryStewardName: finalIdea.inventoryStewardName || null,
      inventoryStorageLocation: finalIdea.inventoryStorageLocation || null,
      externalPartnerName: finalIdea.externalPartnerName || null,
      externalPartnerVerification: finalIdea.externalPartnerVerification || null,
      proposalPhase: finalIdea.proposalPhase || null,
      safetyPlanSummary: finalIdea.safetyPlanSummary || null,
      shapeDetailsJson,
    };

    try {
      await createCampaign(campaignData);
      closeModal();
    } catch (submitError) {
      setError('Failed to post idea. Please try again.');
    }
  };

  const renderCheckboxGroup = (key, legend) => (
    <fieldset className="idea-chip-fieldset">
      <legend>{legend}</legend>
      <div className="idea-chip-grid">
        {checkboxOptions[key].map(([value, label]) => (
          <label key={value} className="idea-check-chip">
            <input
              type="checkbox"
              checked={idea[key].includes(value)}
              onChange={(event) => updateIdea(key, updateArrayValue(idea[key], value, event.target.checked))}
            />
            <span>{label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );

  const renderGuidedForm = () => (
    <div className="idea-compose-grid">
      <section className="idea-form-card idea-form-card-wide">
        <h3>Start with the heart of it</h3>
        <p className="idea-card-helper">
          Keep simple ideas simple. If the idea touches money, public reputation, children, required roles, or standing rules,
          add enough detail for the group to notice the real impact.
        </p>
        <label>
          Idea title
          <input value={idea.title} onChange={(event) => updateIdea('title', event.target.value)} />
          {errors.title && <span className="error-text">{errors.title}</span>}
        </label>
        <label>
          What should the group consider?
          <textarea value={idea.proposal} onChange={(event) => updateIdea('proposal', event.target.value)} />
          {errors.proposal && <span className="error-text">{errors.proposal}</span>}
        </label>
        <label>
          Why does it matter?
          <textarea value={idea.purpose} onChange={(event) => updateIdea('purpose', event.target.value)} />
        </label>
      </section>

      <section className="idea-form-card">
        <h3>What kind of idea is it?</h3>
        <div className="idea-type-grid">
          {ideaTypes.map((type) => (
            <button
              type="button"
              key={type.value}
              className={`idea-type-card ${idea.campaignType === type.value ? 'selected' : ''}`}
              onClick={() => updateIdea('campaignType', type.value)}
            >
              <strong>{type.label}</strong>
              <span>{type.hint}</span>
            </button>
          ))}
        </div>
        <label>
          Real-world shape
          <select value={idea.ideaShape} onChange={(event) => updateIdea('ideaShape', event.target.value)}>
            {ideaShapes.map((shape) => (
              <option key={shape.value} value={shape.value}>{shape.label}</option>
            ))}
          </select>
          <span className="idea-field-hint">
            {ideaShapes.find((shape) => shape.value === idea.ideaShape)?.hint}
          </span>
        </label>
        <label>
          Subtype or plain-language tag
          <input
            value={idea.ideaSubtype}
            onChange={(event) => updateIdea('ideaSubtype', event.target.value)}
            placeholder="Bake sale, roof repair, inter-church dinner, keyboard, well, school..."
          />
        </label>
      </section>

      <section className="idea-form-card">
        <h3>Who is affected?</h3>
        <select value={idea.affectedScope} onChange={(event) => updateIdea('affectedScope', event.target.value)}>
          {Object.entries(scopeLabels).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <label>
          How long would this last?
          <select value={idea.duration} onChange={(event) => updateIdea('duration', event.target.value)}>
            {Object.entries(durationLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <label className="idea-toggle-row">
          <input
            type="checkbox"
            checked={idea.usesGroupNamePublicly}
            onChange={(event) => updateIdea('usesGroupNamePublicly', event.target.checked)}
          />
          <span>This uses the group’s name publicly</span>
        </label>
        <label className="idea-toggle-row">
          <input
            type="checkbox"
            checked={idea.changesPolicy}
            onChange={(event) => updateIdea('changesPolicy', event.target.checked)}
          />
          <span>This creates or changes a standing policy/rule</span>
        </label>
      </section>

      <section className="idea-form-card">
        <h3>Money and stewardship</h3>
        <label>
          Funding path
          <select
            value={idea.fundingPlan}
            onChange={(event) => {
              const nextPlan = event.target.value;
              updateIdea('fundingPlan', nextPlan);
              if (nextPlan === 'treasury' || nextPlan === 'mixed') {
                updateIdea('treasuryFundingRequested', true);
              }
              if (nextPlan === 'separate' || nextPlan === 'mixed') {
                updateIdea('raisesSeparateFunds', true);
              }
            }}
          >
            {displayedFundingPlanOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <label>
          Group funds requested
          <input
            type="number"
            min="0"
            step="0.01"
            value={idea.groupFundsAmount}
            onChange={(event) => updateIdea('groupFundsAmount', event.target.value)}
            placeholder="0.00"
          />
        </label>
        <label className="idea-toggle-row">
          <input
            type="checkbox"
            checked={idea.treasuryFundingRequested}
            onChange={(event) => updateIdea('treasuryFundingRequested', event.target.checked)}
          />
          <span>This may use group treasury funds if approved</span>
        </label>
        {idea.treasuryFundingRequested && (
          <label>
            Treasury cap or maximum spend
            <input
              type="number"
              min="0"
              step="0.01"
              value={idea.treasuryFundingCap}
              onChange={(event) => updateIdea('treasuryFundingCap', event.target.value)}
              placeholder="Maximum amount the group may spend"
            />
          </label>
        )}
        <label>
          Approx. percent of group funds
          <input
            type="number"
            min="0"
            step="0.1"
            value={idea.groupFundsPercent}
            onChange={(event) => updateIdea('groupFundsPercent', event.target.value)}
            placeholder="Leave blank if unknown"
          />
        </label>
        <label className="idea-toggle-row">
          <input
            type="checkbox"
            checked={idea.raisesSeparateFunds}
            onChange={(event) => updateIdea('raisesSeparateFunds', event.target.checked)}
          />
          <span>Money would be raised separately</span>
        </label>
        {idea.raisesSeparateFunds && (
          <>
            <label>
              Separate fundraising target
              <input
                type="number"
                min="0"
                step="0.01"
                value={idea.separateRaiseAmount}
                onChange={(event) => updateIdea('separateRaiseAmount', event.target.value)}
              />
            </label>
            <label>
              Goal for designated giving
              <input
                type="number"
                min="0"
                step="0.01"
                value={idea.separateFundingGoal}
                onChange={(event) => updateIdea('separateFundingGoal', event.target.value)}
                placeholder="Example: 2500.00"
              />
            </label>
            <label className="idea-toggle-row">
              <input
                type="checkbox"
                checked={idea.publicFundraising}
                onChange={(event) => updateIdea('publicFundraising', event.target.checked)}
              />
              <span>This would raise funds publicly in connection with the group</span>
            </label>
          </>
        )}
      </section>

      <section className="idea-form-card">
        <h3>People and responsibility</h3>
        <label>
          About how many helpers?
          <input
            type="number"
            min="0"
            value={idea.volunteerCount}
            onChange={(event) => updateIdea('volunteerCount', event.target.value)}
            placeholder="0"
          />
        </label>
        {renderCheckboxGroup('requiredRoles', 'Required roles')}
      </section>

      <section className="idea-form-card idea-form-card-wide">
        {renderCheckboxGroup('resourceFlags', 'What will it require?')}
        {renderCheckboxGroup('riskFlags', 'Concerns the group should notice')}
      </section>

      <section className="idea-form-card idea-form-card-wide">
        <h3>Special details if this applies</h3>
        <div className="idea-compose-grid idea-compose-grid-compact">
          <label className="idea-toggle-row">
            <input
              type="checkbox"
              checked={idea.inventoryAcquisitionRequested}
              onChange={(event) => updateIdea('inventoryAcquisitionRequested', event.target.checked)}
            />
            <span>This may become a group-owned inventory item</span>
          </label>
          {idea.inventoryAcquisitionRequested && (
            <>
              <label>
                Who would steward/store it?
                <input
                  value={idea.inventoryStewardName}
                  onChange={(event) => updateIdea('inventoryStewardName', event.target.value)}
                  placeholder="Music leader, equipment steward, church office..."
                />
              </label>
              <label>
                Storage location
                <input
                  value={idea.inventoryStorageLocation}
                  onChange={(event) => updateIdea('inventoryStorageLocation', event.target.value)}
                  placeholder="Sanctuary closet, fellowship hall, group storage..."
                />
              </label>
            </>
          )}
          <label>
            External partner/vendor, if any
            <input
              value={idea.externalPartnerName}
              onChange={(event) => updateIdea('externalPartnerName', event.target.value)}
              placeholder="Driller, contractor, neighboring church, local partner..."
            />
          </label>
          <label>
            Partner/quote/permission notes
            <textarea
              value={idea.externalPartnerVerification}
              onChange={(event) => updateIdea('externalPartnerVerification', event.target.value)}
              placeholder="What homework has been done? Quote, permission, contact, ministry relationship, recipient consent..."
            />
          </label>
          <label>
            Phase
            <select value={idea.proposalPhase} onChange={(event) => updateIdea('proposalPhase', event.target.value)}>
              <option value="proposal">Ready proposal</option>
              <option value="assessment">Assessment / homework first</option>
              <option value="pilot">Pilot / small test</option>
              <option value="launch">Launch</option>
              <option value="ongoing">Ongoing stewardship</option>
            </select>
          </label>
          <label>
            Safety / protection / care notes
            <textarea
              value={idea.safetyPlanSummary}
              onChange={(event) => updateIdea('safetyPlanSummary', event.target.value)}
              placeholder="For bonfires, roof repair, children, travel, crisis response, trafficking contexts, privacy, permits, or aftercare."
            />
          </label>
        </div>
      </section>
    </div>
  );

  const renderAiInterview = () => {
    const liveDraft = buildIdeaFromInterview(idea, aiMessages);
    const hasUserMessages = aiMessages.some((message) => message.role === 'user');
    return (
      <section className="idea-ai-panel">
        <div className="idea-ai-heading">
          <p className="idea-eyebrow">Guided conversation</p>
          <h3>Talk with the Idea Guide</h3>
          <p>
            Talk naturally, like you would with the Kingdom Guide. The Idea Guide will ask one useful question at a time, quietly keep a living proposal draft, and hand you an editable form before anything is posted.
          </p>
        </div>
        <div className="idea-ai-chat" ref={aiChatRef}>
          {aiMessages.map((message, index) => (
            <div key={`${message.role}-${index}`} className={`idea-ai-message ${message.role}`}>
              <strong>{message.role === 'assistant' ? 'Idea Guide' : 'You'}</strong>
              <p>{message.content}</p>
            </div>
          ))}
        </div>

        {aiReadyToDraft && (
          <div className="idea-ai-draft-ready">
            <strong>Draft is ready</strong>
            <p>
              The proposal is clear enough to move into the editable form. You can still keep talking if something important is missing.
            </p>
            {aiMissingFields.length > 0 && (
              <p>Still worth clarifying: {aiMissingFields.slice(0, 4).join(', ')}.</p>
            )}
          </div>
        )}

        <label className="idea-ai-input">
          <span>Message Idea Guide</span>
          <textarea
            value={aiInput}
            placeholder="Type naturally. For example: We want to host a Christmas outreach with cocoa, carols, and invitations for lonely neighbors..."
            onChange={(event) => setAiInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                sendAiInterviewAnswer();
              }
            }}
          />
        </label>

        {hasUserMessages && (
          <div className="idea-ai-live-draft">
            <div>
              <p className="idea-eyebrow">Living draft</p>
              <strong>{liveDraft.title || 'Untitled idea'}</strong>
              <p>{liveDraft.proposal || 'The proposal will sharpen as you talk through it.'}</p>
            </div>
            <dl>
              <div>
                <dt>Shape</dt>
                <dd>{ideaShapes.find((shape) => shape.value === liveDraft.ideaShape)?.label || 'Simple idea'}</dd>
              </div>
              <div>
                <dt>Type</dt>
                <dd>{ideaTypes.find((type) => type.value === liveDraft.campaignType)?.label || 'Action or project'}</dd>
              </div>
              <div>
                <dt>Impact path</dt>
                <dd>{evaluateIdeaImpact(liveDraft).level}</dd>
              </div>
              <div>
                <dt>Funds</dt>
                <dd>{liveDraft.groupFundsAmount ? `$${liveDraft.groupFundsAmount} group funds` : liveDraft.raisesSeparateFunds ? 'Separate giving/fundraising' : 'Not identified yet'}</dd>
              </div>
              <div>
                <dt>Funding path</dt>
                <dd>{displayedFundingPlanOptions.find((option) => option.value === liveDraft.fundingPlan)?.label || 'Not identified yet'}</dd>
              </div>
              <div>
                <dt>Helpers</dt>
                <dd>{liveDraft.volunteerCount || 'Not identified yet'}</dd>
              </div>
              <div>
                <dt>Responsibilities</dt>
                <dd>{describeList(liveDraft.requiredRoles, checkboxOptions.requiredRoles)}</dd>
              </div>
              <div>
                <dt>Concerns</dt>
                <dd>{describeList(liveDraft.riskFlags, checkboxOptions.riskFlags)}</dd>
              </div>
            </dl>
          </div>
        )}

        <div className="idea-ai-actions">
          {aiMessages.length > 1 && (
            <button
              type="button"
              className="idea-secondary-button"
              onClick={() => {
                setAiMessages(initialAiMessages);
                setAiStep(0);
                setAiInput('');
                setAiReadyToDraft(false);
                setAiMissingFields([]);
              }}
            >
              Start over
            </button>
          )}
          {hasUserMessages && (
            <button type="button" className="idea-primary-button" onClick={applyAiInterviewDraft}>
              {aiReadyToDraft ? 'Use this draft' : 'Use draft so far'}
            </button>
          )}
          <button type="button" className="idea-primary-button" onClick={sendAiInterviewAnswer} disabled={!aiInput.trim() || isReviewingWithGuide}>
            {isReviewingWithGuide ? 'Listening...' : 'Send'}
          </button>
        </div>
        {guideRecommendedPath === 'memberInvitation' && (
          <aside className="idea-guide-calendar-route">
            <strong>This sounds like a personal invitation, not a group decision.</strong>
            <p>If you are hosting it, attendance is optional, and it does not commit the group’s money, people, resources, or name, no vote is needed.</p>
            <button type="button" className="idea-primary-button" onClick={() => { closeModal(); window.location.assign(`/group-members/${groupId}?tab=calendar&newInvitation=1`) }}>Post it directly to the calendar</button>
            <button type="button" className="idea-secondary-button" onClick={() => setGuideRecommendedPath('idea')}>Keep it as an official group Idea</button>
          </aside>
        )}
        <p className="idea-helper-note">
          After the form is filled, the app evaluates impact and shows the participation/approval thresholds before submission.
        </p>
      </section>
    );
  };

  const renderImpactPanel = () => (
    <aside className={`idea-impact-panel impact-${impact.level.toLowerCase()}`}>
      <p className="idea-eyebrow">Impact review</p>
      <h3>{impact.level}</h3>
      <p>{impact.summary}</p>
      <ul>
        {impact.reasons.slice(0, 5).map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
      <div className="idea-threshold-card">
        <strong>Voting path</strong>
        <span>{impact.notice} notice</span>
        <span>{impact.approvalBasis}</span>
        {eligibleMembers > 0 && (
          <span>
            For {eligibleMembers} members: {voteRequirements.minimumVoters} voters minimum, {voteRequirements.yesVotesNeeded} yes needed.
          </span>
        )}
      </div>
      {guideReview && (
        <div className="idea-guide-review">
          <strong>Kingdom Guide review</strong>
          <p>{guideReview.summary || 'The proposal was reviewed for clarity and its impact on the group.'}</p>
          {Array.isArray(guideReview.concerns) && guideReview.concerns.length > 0 && (
            <ul>
              {guideReview.concerns.slice(0, 3).map((concern) => <li key={concern}>{concern}</li>)}
            </ul>
          )}
        </div>
      )}
    </aside>
  );

  const renderReview = () => (
    <div className="idea-review-layout">
      <section className="idea-review-card">
        <p className="idea-eyebrow">Final proposal preview</p>
        <h3>{assessedIdea.title}</h3>
        <p>{assessedIdea.proposal}</p>
        {assessedIdea.purpose && (
          <>
            <h4>Why it matters</h4>
            <p>{assessedIdea.purpose}</p>
          </>
        )}
        <dl className="idea-review-facts">
          <div>
            <dt>Real-world shape</dt>
            <dd>{ideaShapes.find((shape) => shape.value === assessedIdea.ideaShape)?.label || assessedIdea.ideaShape || 'Simple idea'}</dd>
          </div>
          <div>
            <dt>Type</dt>
            <dd>{ideaTypes.find((type) => type.value === assessedIdea.campaignType)?.label || assessedIdea.campaignType}</dd>
          </div>
          <div>
            <dt>Affects</dt>
            <dd>{scopeLabels[assessedIdea.affectedScope]}</dd>
          </div>
          <div>
            <dt>Duration</dt>
            <dd>{durationLabels[assessedIdea.duration]}</dd>
          </div>
          <div>
            <dt>Helpers</dt>
            <dd>{assessedIdea.volunteerCount || 'Not specified'}</dd>
          </div>
          <div>
            <dt>Group funds</dt>
            <dd>{assessedIdea.groupFundsAmount ? `$${assessedIdea.groupFundsAmount}` : 'None listed'}</dd>
          </div>
          <div>
            <dt>Funding path</dt>
            <dd>{displayedFundingPlanOptions.find((option) => option.value === assessedIdea.fundingPlan)?.label || 'Not specified'}</dd>
          </div>
          <div>
            <dt>Inventory</dt>
            <dd>{assessedIdea.inventoryAcquisitionRequested ? 'May become a group-owned item' : 'Not an inventory item'}</dd>
          </div>
          <div>
            <dt>Required roles</dt>
            <dd>{assessedIdea.requiredRoles?.length ? assessedIdea.requiredRoles.join(', ') : 'None listed'}</dd>
          </div>
        </dl>
      </section>
      {renderImpactPanel()}
    </div>
  );

  const modal = showModal ? (
    <div className="post-campaign-modal" role="presentation">
      <div className="post-campaign-modal-content" role="dialog" aria-modal="true" aria-labelledby="post-idea-title">
        <button type="button" className="post-campaign-close" aria-label="Close idea composer" onClick={closeModal}>
          &times;
        </button>

        <header className="idea-composer-header">
          <p className="idea-eyebrow">Group democracy</p>
          <h2 id="post-idea-title" className="post-campaign-title">
            <i className="fa-solid fa-lightbulb" aria-hidden="true" />
            Post an idea
          </h2>
          <p>
            Share what is on your heart. Simple ideas stay quick; higher-impact ideas get enough clarity for the group to decide wisely.
          </p>
        </header>

        {step === 'compose' && (
          <>
            <div className="idea-mode-switch" role="tablist" aria-label="Idea creation mode">
              <button
                type="button"
                className={entryMode === 'ai' ? 'active' : ''}
                onClick={() => setEntryMode('ai')}
              >
                Create with the Guide
              </button>
              <button
                type="button"
                className={entryMode === 'guided' ? 'active' : ''}
                onClick={() => setEntryMode('guided')}
              >
                Edit details
              </button>
            </div>

            <div className="idea-composer-body">
              <main>
                {notice && <p className="idea-impact-notice">{notice}</p>}
                {entryMode === 'guided' ? renderGuidedForm() : renderAiInterview()}
              </main>
              {renderImpactPanel()}
            </div>

            {errors.impact && <span className="error-text">{errors.impact}</span>}
            <footer className="idea-composer-footer">
              <button type="button" className="idea-secondary-button" onClick={closeModal}>Cancel</button>
              <button type="button" className="idea-primary-button" onClick={reviewIdea} disabled={isReviewingWithGuide}>
                {isReviewingWithGuide ? 'Reviewing...' : 'Review final idea'}
              </button>
            </footer>
          </>
        )}

        {step === 'review' && (
          <>
            {error && <p className="idea-impact-warning">{error}</p>}
            {renderReview()}
            <footer className="idea-composer-footer">
              <button type="button" className="idea-secondary-button" onClick={() => setStep('compose')}>
                Edit idea
              </button>
              <button type="button" className="idea-primary-button" onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? 'Posting...' : 'Submit to group'}
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className="post-campaign-container">
      <button className="post-campaign-button" onClick={() => setShowModal(true)}>
        <i className="fa-solid fa-lightbulb" aria-hidden="true" /> Post Idea
      </button>
      {modal && createPortal(modal, document.body)}
    </div>
  );
};
